import { AxiosError, InternalAxiosRequestConfig } from 'axios';
import axiosInstance from './axios';
import { useAuthStore } from '@/stores/authStore';

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Garante um access token válido, coalescendo chamadas concorrentes de refresh.
 * Se um refresh já estiver em andamento, aguarda o mesmo em vez de disparar outro.
 * Retorna o novo access token.
 */
async function ensureFreshToken(): Promise<string | null> {
  if (isRefreshing) {
    // Já existe um refresh em andamento: aguarda a conclusão dele.
    return new Promise<string | null>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  try {
    const { refreshAccessToken } = useAuthStore.getState();
    await refreshAccessToken();
    const { accessToken } = useAuthStore.getState();
    processQueue(null, accessToken);
    return accessToken;
  } catch (error) {
    processQueue(error as Error, null);
    throw error;
  } finally {
    isRefreshing = false;
  }
}

/**
 * Setup axios interceptors for authentication
 */
export function setupAxiosInterceptors() {
  // Request interceptor - inject access token (com refresh proativo)
  axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const state = useAuthStore.getState();
      const { accessToken, refreshToken } = state;

      // Refresh proativo: se o token está prestes a expirar e temos refresh token,
      // renova ANTES de enviar a requisição, evitando um 401 desnecessário no backend.
      // Não faz refresh proativo em endpoints de auth (login/refresh/logout).
      const isAuthEndpoint = config.url?.includes('/auth/');
      if (accessToken && refreshToken && !isAuthEndpoint && state.isAccessTokenExpiring()) {
        try {
          await ensureFreshToken();
        } catch {
          // Se o refresh proativo falhar, segue com o token atual;
          // o interceptor de response cuidará do 401 (e do logout, se necessário).
        }
      }

      const currentToken = useAuthStore.getState().accessToken;
      if (currentToken && config.headers) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle 401 and refresh token
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<any>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Extract error message from backend response and preserve it
      if (error.response?.data) {
        const backendError = error.response.data;
        
        console.log('Axios interceptor - Backend error:', backendError);
        
        // If backend provides a message, preserve it in the error object
        if (backendError.message) {
          error.message = backendError.message;
        } else if (backendError.error) {
          error.message = backendError.error;
        } else if (typeof backendError === 'string') {
          error.message = backendError;
        }
      }

      // If error is 401 and we haven't retried yet
      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        // Skip refresh for auth endpoints
        if (originalRequest.url?.includes('/auth/')) {
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        try {
          const accessToken = await ensureFreshToken();

          if (originalRequest.headers && accessToken) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }

          return axiosInstance(originalRequest);
        } catch (refreshError) {
          // Logout user on refresh failure
          const { logout } = useAuthStore.getState();
          await logout();

          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
}
